/**
 * Implements Gift with Purchase functionality.
 */
(() => {
  const selectors = {
    success: '[js-gwp-success]',
    threshold: '[js-gwp-threshold]',
    moneyLeft: '[js-money-left]',
  };

  const CURRENCY_RATE = Number(window.Shopify?.currency?.rate) || 1;

  // Get GWP settings
  const {
    DISCOUNT_PRODUCT: GWP_PRODUCT,
    FREE_PRODUCT,

    THRESHOLD_EURO,
    THRESHOLD_DOLLAR,

    FREE_THRESHOLD_EURO,
    FREE_THRESHOLD_DOLLAR,

    CURRENCY
  } = window.GWP;

const giftVariantID = GWP_PRODUCT?.giftVariantID || (GWP_PRODUCT?.variants?.length ? GWP_PRODUCT?.variants[0]?.id : null);
const freeVariantID = FREE_PRODUCT?.freeVariantID || (FREE_PRODUCT?.variants?.length ? FREE_PRODUCT?.variants[0]?.id : null);

  // Determine the thresholds based on currency
  let threshold;
  let free_threshold;

  switch (CURRENCY.iso_code) {
    case 'USD':
      threshold = THRESHOLD_DOLLAR;
      free_threshold = FREE_THRESHOLD_DOLLAR;
      break;
    case 'EUR':
      threshold = THRESHOLD_EURO;
      free_threshold = FREE_THRESHOLD_EURO;
      break;
    default:
      threshold = THRESHOLD_EURO * CURRENCY_RATE;
      free_threshold = FREE_THRESHOLD_EURO * CURRENCY_RATE;
      break;
  }

  if (!freeVariantID && !giftVariantID) {
    return;
  }

  /**
   * Handles the update of the cart drawer.
   * @param {Event} e - The event object.
   * @async
   */
  const handleCartUpdate = async (e) => {
    const cart = e.detail;
    const total = parseFloat(cart.total_price);
    const gwpProduct = getGWP(cart.items);
    let moneyToSpend = 0;

    if (total < free_threshold) {
      await removeProduct(gwpProduct?.key)

      moneyToSpend = +((free_threshold - total) / 100).toFixed(2);
      showThresholdMessage(`${moneyToSpend}${CURRENCY.symbol}`);
    }

    if (total >= free_threshold && (total < threshold || !giftVariantID)) {
      if (gwpProduct && giftVariantID && +gwpProduct.variant_id !== +freeVariantID) {
        await removeProduct(gwpProduct.key)
      }

      if (gwpProduct && +gwpProduct.variant_id === +freeVariantID) {
        updateGiftProductLayout()
      }

      if (!gwpProduct) {
        await addGwpProduct(freeVariantID);
      }

      moneyToSpend = +((threshold - total) / 100).toFixed(2);
      showThresholdMessage(`${moneyToSpend}${CURRENCY.symbol}`);
    }

    if (total >= threshold && giftVariantID) {
      if (gwpProduct && freeVariantID && +gwpProduct.variant_id !== +giftVariantID) {
        await removeProduct(gwpProduct.key)
      }

      if (gwpProduct && +gwpProduct.variant_id === +giftVariantID) {
        const totalWithoutGWP = total - gwpProduct.final_price;

        if (totalWithoutGWP >= threshold) {
          updateGiftProductLayout()
          showSuccessMessage()
        } else {
          await removeProduct(gwpProduct.key)
        }
      }

      if (!gwpProduct) {
        await addGwpProduct(giftVariantID);
      }
    }
  };

  const updateGiftProductLayout = () => {
    const gwpProductTitle = document.querySelector('.cart-item.cart-gift-item .cart-item-product-title');
    const gwpProductImage = document.querySelector('.cart-item.cart-gift-item .cart-item-image');
    const gwpItemEl = document.querySelector('.cart-item.cart-gift-item')
    const { gwpCustomText, gwpCustomImage, gwpCustomIcon } = window.theme.cart;

    if (gwpProductTitle && gwpCustomText) gwpProductTitle.textContent = gwpCustomText;
    if (gwpProductImage && gwpCustomImage) gwpProductImage.src = gwpCustomImage;
    if (gwpCustomIcon) {
      const icon = document.createElement('img')
      icon.src = gwpCustomIcon
      icon.classList.add('gwp-icon')
      gwpItemEl?.appendChild(icon)
    }
  }

  /**
   * Checks if the GWP product is already added to the cart.
   * @param {Array} items - The items in the cart.
   * @returns {object|boolean} - Whether the GWP product is added - item, otherwise `false`.
   */
  const getGWP = (items) => {
    if (!items || !items?.length) {
      return false;
    }

    const gwpProduct = items.find((item) => item.properties?._gwp) || false;

    return gwpProduct;
  };

  /**
   * Adds the GWP product to the cart.
   * @param {String|Number} variantID - variant ID to add.
   * @async
   */
  const addGwpProduct = async (variantID) => {
    try {
      await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantID,
              quantity: 1,
              properties: {
                _gwp: true,
              },
            },
          ],
        }),
      });

      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cartStateBody = await response.json();

      window.wetheme.updateCartDrawer(cartStateBody);
    } catch (error) {
      throw new Error('Failed to add GWP product to cart', error);
    }
  };

  /**
   * Remove the product from the cart.
   * @param {string} key - line item key.
   * @async
   */
  const removeProduct = async (key) => {
    if (!key) {
      return;
    }

    const updates = {
      [key]: 0,
    };

    const gwpPrice = document.querySelector('.cart-item.cart-gift-item .cart-item-price')
    gwpPrice && gwpPrice.classList.add('removing')

    try {
      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      });

      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cartStateBody = await response.json();

      window.wetheme.updateCartDrawer(cartStateBody);
    } catch (error) {
      throw new Error('Failed to remove GWP product', error);
    }
  };

  /**
   * Shows a success message.
   */
  const showSuccessMessage = () => {
    const successNode = document.querySelector(selectors.success);
    const thresholdNode = document.querySelector(selectors.threshold);

    successNode?.classList.remove('hidden');
    thresholdNode?.classList.add('hidden');
  };

  /**
   * Shows an error message indicating the money left to spend.
   * @param {string} moneyLeft - The amount of money left to spend.
   */
  const showThresholdMessage = (moneyLeft) => {
    const successNode = document.querySelector(selectors.success);
    const thresholdNode = document.querySelector(selectors.threshold);
    const moneyLeftNode = document.querySelector(selectors.moneyLeft);

    successNode?.classList.add('hidden');
    thresholdNode?.classList.remove('hidden');

    if (!moneyLeftNode || !moneyLeft) {
      return;
    }

    moneyLeftNode.textContent = moneyLeft;
  };

  // Listen for cart drawer updates
  document.addEventListener('update-cart-drawer', handleCartUpdate);
})();
